'use client';
import React, { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { PaymentIntent } from '@stripe/stripe-js';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { getStripe } from '@/lib/stripe/client';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ConsultantService {
  id: string;
  name: string;
  initials: string;
  title: string;
  category: string;
  description: string;
  skills: string[];
  rate: string;
  rateType: 'hourly' | 'project' | 'retainer';
  rating: number;
  reviewCount: number;
  location: string;
  languages: string[];
  verified: boolean;
  badges: string[];
  availability: 'available' | 'busy' | 'limited';
  memberSince: string;
  completedProjects: number;
}

interface ServiceCategory {
  id: string;
  label: string;
  icon: string;
  count: number;
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const categories: ServiceCategory[] = [
  { id: 'all', label: 'All Services', icon: 'Squares2X2Icon', count: 24 },
  { id: 'finance', label: 'Islamic Finance', icon: 'BanknotesIcon', count: 6 },
  { id: 'legal', label: 'Halal Legal', icon: 'ScaleIcon', count: 4 },
  { id: 'business', label: 'Business Strategy', icon: 'BriefcaseIcon', count: 5 },
  { id: 'tech', label: 'Tech & Digital', icon: 'ComputerDesktopIcon', count: 5 },
  { id: 'marketing', label: 'Halal Marketing', icon: 'MegaphoneIcon', count: 4 },
];

const consultants: ConsultantService[] = [
  {
    id: '1',
    name: 'Yusuf Al-Rashid',
    initials: 'YR',
    title: 'Islamic Finance & Sukuk Structuring Expert',
    category: 'finance',
    description: 'Certified Islamic finance professional with 12 years structuring Sukuk, Murabaha, and Musharakah instruments for SMEs and corporates across GCC and Southeast Asia.',
    skills: ['Sukuk Structuring', 'Murabaha', 'Zakat Calculation', 'AAOIFI Standards', 'Shariah Audit'],
    rate: '$150',
    rateType: 'hourly',
    rating: 4.9,
    reviewCount: 87,
    location: 'Kuala Lumpur, Malaysia',
    languages: ['English', 'Arabic', 'Malay'],
    verified: true,
    badges: ['Shariah Certified', 'Top Consultant', 'Fast Responder'],
    availability: 'available',
    memberSince: 'Jan 2024',
    completedProjects: 143,
  },
  {
    id: '2',
    name: 'Fatima Okonkwo',
    initials: 'FO',
    title: 'Halal Business Strategy & Market Entry',
    category: 'business',
    description: 'MBA-qualified strategist helping Muslim entrepreneurs enter African and European halal markets. Specialises in halal certification pathways and distribution networks.',
    skills: ['Market Entry', 'Halal Certification', 'Business Plans', 'Pitch Decks', 'OIC Markets'],
    rate: '$120',
    rateType: 'hourly',
    rating: 4.8,
    reviewCount: 62,
    location: 'Lagos, Nigeria',
    languages: ['English', 'French', 'Yoruba'],
    verified: true,
    badges: ['Halal Certified', 'Trusted Partner'],
    availability: 'limited',
    memberSince: 'Mar 2024',
    completedProjects: 89,
  },
  {
    id: '3',
    name: 'Ibrahim Siddiqui',
    initials: 'IS',
    title: 'Full-Stack Developer — Islamic Fintech',
    category: 'tech',
    description: 'Senior developer building Shariah-compliant fintech apps, Islamic banking portals, and Zakat management systems. Expert in Next.js, Node.js, and blockchain for halal finance.',
    skills: ['Next.js', 'Node.js', 'Blockchain', 'Fintech APIs', 'Zakat Systems'],
    rate: '$95',
    rateType: 'hourly',
    rating: 4.7,
    reviewCount: 54,
    location: 'Karachi, Pakistan',
    languages: ['English', 'Urdu', 'Arabic'],
    verified: true,
    badges: ['ID Verified', 'Fast Responder'],
    availability: 'available',
    memberSince: 'Feb 2024',
    completedProjects: 67,
  },
  {
    id: '4',
    name: 'Aisha Bello',
    initials: 'AB',
    title: 'Halal Legal Advisor & Contract Specialist',
    category: 'legal',
    description: 'Barrister specialising in Islamic commercial law, halal business contracts, and Shariah-compliant corporate structures across West Africa and the UK.',
    skills: ['Islamic Contract Law', 'Halal Compliance', 'Corporate Structures', 'Dispute Resolution', 'DIFC Law'],
    rate: '$180',
    rateType: 'hourly',
    rating: 4.9,
    reviewCount: 41,
    location: 'Abuja, Nigeria',
    languages: ['English', 'Hausa', 'Arabic'],
    verified: true,
    badges: ['Shariah Certified', 'Trusted Partner', 'Top Consultant'],
    availability: 'limited',
    memberSince: 'Apr 2024',
    completedProjects: 52,
  },
  {
    id: '5',
    name: 'Omar Farouq',
    initials: 'OF',
    title: 'Halal Digital Marketing & SEO Strategist',
    category: 'marketing',
    description: 'Growth marketer helping halal brands reach Muslim consumers globally. Expert in Islamic content marketing, social media strategy, and halal e-commerce growth.',
    skills: ['SEO', 'Content Marketing', 'Social Media', 'Halal Branding', 'E-commerce'],
    rate: '$85',
    rateType: 'hourly',
    rating: 4.6,
    reviewCount: 73,
    location: 'Cairo, Egypt',
    languages: ['English', 'Arabic', 'French'],
    verified: true,
    badges: ['ID Verified', 'Fast Responder'],
    availability: 'available',
    memberSince: 'May 2024',
    completedProjects: 98,
  },
  {
    id: '6',
    name: 'Zainab Hassan',
    initials: 'ZH',
    title: 'Islamic Finance Compliance & Audit',
    category: 'finance',
    description: 'Certified Shariah auditor with expertise in AAOIFI standards, Islamic banking compliance, and Zakat fund management for institutions and NGOs.',
    skills: ['Shariah Audit', 'AAOIFI', 'Zakat Management', 'Islamic Banking', 'Compliance'],
    rate: '$130',
    rateType: 'hourly',
    rating: 4.8,
    reviewCount: 38,
    location: 'Dubai, UAE',
    languages: ['English', 'Arabic', 'Swahili'],
    verified: true,
    badges: ['Shariah Certified', 'Halal Certified'],
    availability: 'busy',
    memberSince: 'Jun 2024',
    completedProjects: 44,
  },
];

const BADGE_STYLES: Record<string, string> = {
  'Shariah Certified': 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  'Top Consultant': 'bg-amber-50 text-amber-700 border border-amber-200',
  'Fast Responder': 'bg-blue-50 text-blue-700 border border-blue-200',
  'Halal Certified': 'bg-green-50 text-green-700 border border-green-200',
  'Trusted Partner': 'bg-purple-50 text-purple-700 border border-purple-200',
  'ID Verified': 'bg-sky-50 text-sky-700 border border-sky-200',
};

const AVAILABILITY_CONFIG = {
  available: { label: 'Available', dot: 'bg-emerald-500', text: 'text-emerald-700' },
  limited: { label: 'Limited slots', dot: 'bg-amber-500', text: 'text-amber-700' },
  busy: { label: 'Fully booked', dot: 'bg-red-400', text: 'text-red-600' },
};

// ─── Sub-components ────────────────────────────────────────────────────────────
function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-3 h-3 ${star <= Math.round(rating) ? 'text-amber-400' : 'text-muted'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

// ─── Stripe Payment Form ───────────────────────────────────────────────────────
interface PaymentFormProps {
  clientSecret: string;
  onSuccess: (pi: PaymentIntent) => void;
  onError: (msg: string) => void;
  isProcessing: boolean;
  setIsProcessing: (v: boolean) => void;
}

function ConsultationPaymentForm({ clientSecret, onSuccess, onError, isProcessing, setIsProcessing }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/freelance-consultants` },
      redirect: 'if_required',
    });

    if (error) {
      onError(error.message ?? 'Payment failed. Please try again.');
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      // Confirm on backend to transfer funds to consultant wallet
      try {
        const supabase = createClient();
        await supabase.functions.invoke('confirm-consultation-payment', {
          body: { paymentIntentId: paymentIntent.id },
        });
      } catch (_) {
        // Non-blocking — webhook will handle it as fallback
      }
      onSuccess(paymentIntent);
    }
    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full py-3 rounded-xl gradient-teal text-white text-sm font-700 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isProcessing ? (
          <>
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Processing...
          </>
        ) : (
          <>
            <Icon name="LockClosedIcon" size={14} />
            Pay Now — Secure Checkout
          </>
        )}
      </button>
      <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
        <Icon name="ShieldCheckIcon" size={12} />
        Secured by Stripe · Payment captured at confirmation
      </p>
    </form>
  );
}

// ─── Booking + Checkout Modal ──────────────────────────────────────────────────
interface BookingCheckoutModalProps {
  consultant: ConsultantService;
  onClose: () => void;
}

const stripePromise = getStripe();

function BookingCheckoutModal({ consultant, onClose }: BookingCheckoutModalProps) {
  const [step, setStep] = useState<'details' | 'payment' | 'success'>('details');
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    notes: '',
    sessionType: '1hr',
  });

  const rateNum = parseFloat(consultant.rate.replace(/[^0-9.]/g, ''));
  const sessionOptions = [
    { value: '1hr', label: '1 Hour Session', multiplier: 1 },
    { value: '2hr', label: '2 Hour Session', multiplier: 2 },
    { value: 'half-day', label: 'Half Day (4hrs)', multiplier: 4 },
  ];
  const selectedSession = sessionOptions.find((s) => s.value === form.sessionType) ?? sessionOptions[0];
  const totalAmount = rateNum * selectedSession.multiplier;

  const handleProceedToPayment = async () => {
    if (!form.firstName || !form.email) {
      setErrorMsg('Please fill in your name and email.');
      return;
    }
    setErrorMsg('');
    setIsLoading(true);

    try {
      const supabase = createClient();
      const { data, error } = await supabase.functions.invoke<{
        clientSecret: string;
        paymentIntentId: string;
        stripeCustomerId: string;
      }>('create-consultation-payment', {
        body: {
          consultantId: consultant.id,
          consultantName: consultant.name,
          serviceTitle: consultant.title,
          amountUsd: totalAmount,
          currency: 'usd',
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
        },
      });

      if (error || !data?.clientSecret) {
        setErrorMsg((data as { error?: string })?.error ?? 'Could not initialise payment. Please try again.');
        setIsLoading(false);
        return;
      }

      setClientSecret(data.clientSecret);
      setPaymentIntentId(data.paymentIntentId);
      setStep('payment');
    } catch (err) {
      setErrorMsg('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = (_pi: PaymentIntent) => {
    setStep('success');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-base font-700 text-foreground">
              {step === 'details' && 'Book Consultation'}
              {step === 'payment' && 'Secure Payment'}
              {step === 'success' && 'Booking Confirmed!'}
            </h2>
            {step !== 'success' && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {step === 'details' ? 'Step 1 of 2 — Session details' : 'Step 2 of 2 — Payment'}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>

        {/* Progress bar */}
        {step !== 'success' && (
          <div className="h-1 bg-muted">
            <div
              className="h-full gradient-teal transition-all duration-300"
              style={{ width: step === 'details' ? '50%' : '100%' }}
            />
          </div>
        )}

        <div className="px-6 py-5 space-y-4">
          {/* Consultant summary */}
          {step !== 'success' && (
            <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
              <div className="w-10 h-10 rounded-xl gradient-teal flex items-center justify-center text-white font-700 text-sm flex-shrink-0">
                {consultant.initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-600 text-foreground">{consultant.name}</p>
                <p className="text-xs text-muted-foreground line-clamp-1">{consultant.title}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-700 text-foreground">{consultant.rate}/hr</p>
              </div>
            </div>
          )}

          {/* Step 1: Details */}
          {step === 'details' && (
            <>
              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Session Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {sessionOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setForm({ ...form, sessionType: opt.value })}
                      className={`py-2.5 px-2 rounded-xl border text-xs font-600 transition-all duration-150 ${
                        form.sessionType === opt.value
                          ? 'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      <div>{opt.label.split(' ')[0]} {opt.label.split(' ')[1]}</div>
                      <div className="text-xs font-700 mt-0.5">${rateNum * opt.multiplier}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">First Name *</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    placeholder="Yusuf"
                    className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">Last Name</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    placeholder="Al-Rashid"
                    className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Email Address *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Notes for Consultant (optional)</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Briefly describe what you need help with..."
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>

              {/* Order summary */}
              <div className="bg-muted/60 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{selectedSession.label}</span>
                  <span className="font-600 text-foreground">${rateNum * selectedSession.multiplier}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Platform fee (10%)</span>
                  <span>${(totalAmount * 0.10).toFixed(2)}</span>
                </div>
                <div className="border-t border-border pt-2 flex justify-between text-sm font-700">
                  <span className="text-foreground">Total</span>
                  <span className="text-primary">${totalAmount.toFixed(2)} USD</span>
                </div>
              </div>

              {errorMsg && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{errorMsg}</p>
              )}

              <button
                onClick={handleProceedToPayment}
                disabled={isLoading}
                className="w-full py-3 rounded-xl gradient-teal text-white text-sm font-700 hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Preparing checkout...
                  </>
                ) : (
                  <>
                    Proceed to Payment
                    <Icon name="ArrowRightIcon" size={14} />
                  </>
                )}
              </button>
            </>
          )}

          {/* Step 2: Payment */}
          {step === 'payment' && clientSecret && (
            <>
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-start gap-2">
                <Icon name="InformationCircleIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <p className="text-xs text-foreground">
                  Payment of <strong>${totalAmount.toFixed(2)} USD</strong> will be captured immediately.
                  Funds are transferred to {consultant.name.split(' ')[0]} upon session completion.
                </p>
              </div>

              {errorMsg && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{errorMsg}</p>
              )}

              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                <ConsultationPaymentForm
                  clientSecret={clientSecret}
                  onSuccess={handlePaymentSuccess}
                  onError={setErrorMsg}
                  isProcessing={isProcessing}
                  setIsProcessing={setIsProcessing}
                />
              </Elements>

              <button
                onClick={() => { setStep('details'); setErrorMsg(''); }}
                className="w-full py-2 rounded-xl border border-border text-sm text-muted-foreground hover:bg-muted transition-colors"
              >
                ← Back to Details
              </button>
            </>
          )}

          {/* Step 3: Success */}
          {step === 'success' && (
            <div className="text-center py-6 space-y-4">
              <div className="w-16 h-16 rounded-full gradient-teal flex items-center justify-center mx-auto">
                <Icon name="CheckCircleIcon" size={32} className="text-white" />
              </div>
              <div>
                <h3 className="text-base font-700 text-foreground mb-1">Booking Confirmed!</h3>
                <p className="text-sm text-muted-foreground">
                  Your consultation with <strong>{consultant.name}</strong> is confirmed.
                  Payment has been captured and will be transferred to the consultant upon session completion.
                </p>
              </div>
              <div className="bg-muted/60 rounded-xl p-4 text-left space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Consultant</span>
                  <span className="font-600 text-foreground">{consultant.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Session</span>
                  <span className="font-600 text-foreground">{selectedSession.label}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount Paid</span>
                  <span className="font-700 text-primary">${totalAmount.toFixed(2)} USD</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status</span>
                  <span className="text-emerald-600 font-600 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    Paid & Confirmed
                  </span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                A confirmation will be sent to <strong>{form.email}</strong>. The consultant will contact you to schedule the session.
              </p>
              <button
                onClick={onClose}
                className="w-full py-2.5 rounded-xl gradient-teal text-white text-sm font-700 hover:opacity-90 transition-opacity"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Consultant Card ───────────────────────────────────────────────────────────
function ConsultantCard({
  consultant,
  onContact,
  onBook,
}: {
  consultant: ConsultantService;
  onContact: (c: ConsultantService) => void;
  onBook: (c: ConsultantService) => void;
}) {
  const avail = AVAILABILITY_CONFIG[consultant.availability];
  return (
    <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-4 hover:shadow-card transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="relative flex-shrink-0">
          <div className="w-12 h-12 rounded-xl gradient-teal flex items-center justify-center text-white font-700 text-base">
            {consultant.initials}
          </div>
          {consultant.verified && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center border-2 border-card">
              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3 className="text-sm font-700 text-foreground truncate">{consultant.name}</h3>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{consultant.title}</p>
          <div className="flex items-center gap-1 mt-1">
            <span className={`w-1.5 h-1.5 rounded-full ${avail.dot}`} />
            <span className={`text-xs font-500 ${avail.text}`}>{avail.label}</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-base font-700 text-foreground">{consultant.rate}</p>
          <p className="text-xs text-muted-foreground">{consultant.rateType === 'hourly' ? '/hr' : consultant.rateType === 'project' ? '/project' : '/mo'}</p>
        </div>
      </div>

      {/* Badges */}
      {consultant.badges.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {consultant.badges.map((badge) => (
            <span key={badge} className={`text-xs px-2 py-0.5 rounded-full font-500 ${BADGE_STYLES[badge] ?? 'bg-muted text-muted-foreground'}`}>
              {badge}
            </span>
          ))}
        </div>
      )}

      {/* Description */}
      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">{consultant.description}</p>

      {/* Skills */}
      <div className="flex flex-wrap gap-1.5">
        {consultant.skills.slice(0, 4).map((skill) => (
          <span key={skill} className="text-xs px-2.5 py-1 rounded-lg bg-muted text-muted-foreground font-500">
            {skill}
          </span>
        ))}
        {consultant.skills.length > 4 && (
          <span className="text-xs px-2.5 py-1 rounded-lg bg-muted text-muted-foreground font-500">
            +{consultant.skills.length - 4}
          </span>
        )}
      </div>

      {/* Meta */}
      <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-3">
        <div className="flex items-center gap-1">
          <StarRating rating={consultant.rating} />
          <span className="font-600 text-foreground ml-1">{consultant.rating}</span>
          <span>({consultant.reviewCount})</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Icon name="CheckCircleIcon" size={12} />
            {consultant.completedProjects} projects
          </span>
          <span className="flex items-center gap-1">
            <Icon name="MapPinIcon" size={12} />
            {consultant.location.split(',')[1]?.trim() ?? consultant.location}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onContact(consultant)}
          className="flex-1 py-2.5 rounded-xl border border-border text-foreground text-sm font-600 hover:bg-muted transition-colors duration-150"
        >
          Contact
        </button>
        <button
          onClick={() => onBook(consultant)}
          disabled={consultant.availability === 'busy'}
          className="flex-1 py-2.5 rounded-xl gradient-teal text-white text-sm font-600 hover:opacity-90 transition-opacity duration-150 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
        >
          <Icon name="CreditCardIcon" size={14} />
          Book & Pay
        </button>
      </div>
    </div>
  );
}

function ListServiceModal({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState({
    name: '',
    title: '',
    category: '',
    description: '',
    rate: '',
    rateType: 'hourly',
    skills: '',
    languages: '',
    location: '',
  });

  const handleSubmit = () => setStep(3);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-700 text-foreground">
              {step === 3 ? 'Application Submitted!' : 'List Your Services'}
            </h2>
            {step !== 3 && (
              <p className="text-xs text-muted-foreground mt-0.5">Step {step} of 2</p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>

        {step !== 3 && (
          <div className="h-1 bg-muted">
            <div
              className="h-full gradient-teal transition-all duration-300"
              style={{ width: step === 1 ? '50%' : '100%' }}
            />
          </div>
        )}

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {step === 1 && (
            <>
              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Full Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Yusuf Al-Rashid"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Professional Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Islamic Finance & Sukuk Structuring Expert"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Service Category *</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">Select a category</option>
                  <option value="finance">Islamic Finance</option>
                  <option value="legal">Halal Legal</option>
                  <option value="business">Business Strategy</option>
                  <option value="tech">Tech & Digital</option>
                  <option value="marketing">Halal Marketing</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Service Description *</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe your expertise, experience, and what clients can expect..."
                  rows={4}
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">Rate *</label>
                  <input
                    type="text"
                    value={form.rate}
                    onChange={(e) => setForm({ ...form, rate: e.target.value })}
                    placeholder="e.g. 150"
                    className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">Rate Type</label>
                  <select
                    value={form.rateType}
                    onChange={(e) => setForm({ ...form, rateType: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="project">Per Project</option>
                    <option value="retainer">Monthly Retainer</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Skills (comma-separated)</label>
                <input
                  type="text"
                  value={form.skills}
                  onChange={(e) => setForm({ ...form, skills: e.target.value })}
                  placeholder="e.g. Sukuk, Murabaha, Zakat"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Languages</label>
                <input
                  type="text"
                  value={form.languages}
                  onChange={(e) => setForm({ ...form, languages: e.target.value })}
                  placeholder="e.g. English, Arabic"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Location</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="e.g. Kuala Lumpur, Malaysia"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            </>
          )}

          {step === 3 && (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full gradient-teal flex items-center justify-center mx-auto mb-3">
                <Icon name="CheckCircleIcon" size={24} className="text-white" />
              </div>
              <h3 className="text-sm font-700 text-foreground mb-1">Application Submitted!</h3>
              <p className="text-xs text-muted-foreground">
                Our team will review your profile and get back to you within 2-3 business days.
              </p>
              <button onClick={onClose} className="mt-4 px-6 py-2 rounded-xl gradient-teal text-white text-sm font-600 hover:opacity-90 transition-opacity">
                Done
              </button>
            </div>
          )}
        </div>

        {step !== 3 && (
          <div className="px-6 pb-5 flex gap-3">
            {step === 2 && (
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:bg-muted transition-colors"
              >
                Back
              </button>
            )}
            <button
              onClick={step === 1 ? () => setStep(2) : handleSubmit}
              className="flex-1 py-2.5 rounded-xl gradient-teal text-white text-sm font-600 hover:opacity-90 transition-opacity"
            >
              {step === 1 ? 'Next' : 'Submit Application'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ContactModal({ consultant, onClose }: { consultant: ConsultantService; onClose: () => void }) {
  const [message, setMessage] = useState(`Hi ${consultant.name.split(' ')[0]}, I found your profile on Rafeeq and I'm interested in your ${consultant.title} services. Could we schedule a brief call to discuss my needs?`);
  const [sent, setSent] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-700 text-foreground">Contact Consultant</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {!sent ? (
            <>
              <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                <div className="w-10 h-10 rounded-xl gradient-teal flex items-center justify-center text-white font-700 text-sm flex-shrink-0">
                  {consultant.initials}
                </div>
                <div>
                  <p className="text-sm font-600 text-foreground">{consultant.name}</p>
                  <p className="text-xs text-muted-foreground">{consultant.title}</p>
                </div>
              </div>
              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5">Your Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>
              <button
                onClick={() => setSent(true)}
                className="w-full py-2.5 rounded-xl gradient-teal text-white text-sm font-600 hover:opacity-90 transition-opacity"
              >
                Send Message
              </button>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full gradient-teal flex items-center justify-center mx-auto mb-3">
                <Icon name="PaperAirplaneIcon" size={24} className="text-white" />
              </div>
              <h3 className="text-sm font-700 text-foreground mb-1">Message Sent!</h3>
              <p className="text-xs text-muted-foreground">
                {consultant.name.split(' ')[0]} typically responds within {consultant.availability === 'available' ? '2 hours' : '24 hours'}.
              </p>
              <button onClick={onClose} className="mt-4 px-6 py-2 rounded-xl gradient-teal text-white text-sm font-600 hover:opacity-90 transition-opacity">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function FreelanceConsultantsPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showListModal, setShowListModal] = useState(false);
  const [contactConsultant, setContactConsultant] = useState<ConsultantService | null>(null);
  const [bookConsultant, setBookConsultant] = useState<ConsultantService | null>(null);
  const [sortBy, setSortBy] = useState<'rating' | 'rate' | 'projects'>('rating');

  const filtered = consultants
    .filter((c) => {
      const matchesCategory = activeCategory === 'all' || c.category === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.title.toLowerCase().includes(q) ||
        c.skills.some((s) => s.toLowerCase().includes(q)) ||
        c.description.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'projects') return b.completedProjects - a.completedProjects;
      const rateA = parseFloat(a.rate.replace(/[^0-9.]/g, ''));
      const rateB = parseFloat(b.rate.replace(/[^0-9.]/g, ''));
      return rateA - rateB;
    });

  return (
    <AppLayout activeRoute="/freelance-consultants">
      <div className="min-h-screen bg-background">
        {/* Page Header */}
        <div className="bg-card border-b border-border px-6 py-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">🕌</span>
                  <h1 className="text-xl font-700 text-foreground">Muslim Freelance Consultants</h1>
                </div>
                <p className="text-sm text-muted-foreground">
                  Connect with verified halal-conscious professionals across finance, law, tech, and business
                </p>
              </div>
              <button
                onClick={() => setShowListModal(true)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl gradient-teal text-white text-sm font-600 hover:opacity-90 transition-opacity whitespace-nowrap"
              >
                <Icon name="PlusCircleIcon" size={16} />
                Offer Your Services
              </button>
            </div>

            {/* Stats bar */}
            <div className="flex items-center gap-6 mt-5 pt-5 border-t border-border">
              {[
                { label: 'Verified Consultants', value: '24+', icon: '✅' },
                { label: 'Service Categories', value: '5', icon: '📂' },
                { label: 'Projects Completed', value: '520+', icon: '🏆' },
                { label: 'Avg. Rating', value: '4.8★', icon: '⭐' },
              ].map((stat) => (
                <div key={stat.label} className="flex items-center gap-2">
                  <span className="text-base">{stat.icon}</span>
                  <div>
                    <p className="text-sm font-700 text-foreground">{stat.value}</p>
                    <p className="text-xs text-muted-foreground hidden sm:block">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
          {/* Search & Sort */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, skill, or expertise..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="px-3 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="rating">Sort: Top Rated</option>
              <option value="projects">Sort: Most Projects</option>
              <option value="rate">Sort: Lowest Rate</option>
            </select>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-500 whitespace-nowrap transition-all duration-150 ${
                  activeCategory === cat.id
                    ? 'gradient-teal text-white shadow-prayer'
                    : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={cat.icon as Parameters<typeof Icon>[0]['name']} size={14} />
                {cat.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeCategory === cat.id ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'}`}>
                  {cat.count}
                </span>
              </button>
            ))}
          </div>

          {/* Results count */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              <span className="font-600 text-foreground">{filtered.length}</span> consultant{filtered.length !== 1 ? 's' : ''} found
            </p>
          </div>

          {/* Consultant Grid */}
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.map((consultant) => (
                <ConsultantCard
                  key={consultant.id}
                  consultant={consultant}
                  onContact={setContactConsultant}
                  onBook={setBookConsultant}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                <Icon name="MagnifyingGlassIcon" size={24} className="text-muted-foreground" />
              </div>
              <h3 className="text-base font-600 text-foreground mb-1">No consultants found</h3>
              <p className="text-sm text-muted-foreground">Try adjusting your search or category filter</p>
            </div>
          )}

          {/* CTA Banner */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-accent/5 to-primary/5 border border-primary/20 p-6">
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-primary/5 blur-2xl pointer-events-none" />
            <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-base font-700 text-foreground mb-1">Are you a Muslim professional?</h3>
                <p className="text-sm text-muted-foreground max-w-md">
                  Join 24+ verified consultants already offering halal-conscious services to the global Muslim community on Rafeeq.
                </p>
              </div>
              <button
                onClick={() => setShowListModal(true)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl gradient-teal text-white text-sm font-600 hover:opacity-90 transition-opacity whitespace-nowrap"
              >
                <Icon name="UserPlusIcon" size={16} />
                List Your Services
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showListModal && <ListServiceModal onClose={() => setShowListModal(false)} />}
      {contactConsultant && <ContactModal consultant={contactConsultant} onClose={() => setContactConsultant(null)} />}
      {bookConsultant && <BookingCheckoutModal consultant={bookConsultant} onClose={() => setBookConsultant(null)} />}
    </AppLayout>
  );
}
